import axios, { type AxiosInstance } from "axios";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { getFirebaseAuth } from "../firebase";
import { Sentry } from "../sentry";

// Singleton axios client instance for use outside React components (e.g., Redux thunks)
let axiosClientInstance: AxiosInstance | null = null;

/**
 * Initialize the singleton axios client instance
 * @param baseURL Base URL for the API
 * @returns Configured axios instance
 */
export async function initializeAxiosClient(
  baseURL: string,
): Promise<AxiosInstance> {
  if (!axiosClientInstance) {
    axiosClientInstance = await createAuthenticatedAxiosInstance(baseURL);
  }
  return axiosClientInstance;
}

/**
 * Get the singleton axios client instance
 * @returns The axios client instance
 * @throws Error if client is not initialized
 */
export function getAxiosClient(): AxiosInstance {
  if (!axiosClientInstance) {
    throw new Error(
      "Axios client not initialized. Call initializeAxiosClient first.",
    );
  }
  return axiosClientInstance;
}

export interface Links {
  current: string;
  next: string | null;
  previous: string | null;
}

export interface ApiErrorDetails {
  error: string;
  error_detail: string;
}

export interface FullApiErrorResponse {
  errors: ApiErrorDetails;
  body: null;
  status_code: number;
}

export interface ApiVariables<TBody> {
  body: TBody;
}

export interface ApiSuccessBody<TResult> {
  links: Links;
  count: number;
  results: TResult[];
}

export interface FullApiResponseWrapper<TResult> {
  errors: null;
  body: ApiSuccessBody<TResult>;
  status_code: number;
}

/**
 * Creates an axios instance with Firebase authentication token
 * @param urlPath Base URL for the API
 * @returns Configured axios instance with auth interceptor
 */
export async function createAuthenticatedAxiosInstance(
  urlPath: string,
): Promise<AxiosInstance> {
  const auth = getFirebaseAuth();

  const instance = axios.create({
    baseURL: urlPath,
  });

  // Add request interceptor to include auth token
  instance.interceptors.request.use(
    async (config) => {
      const isFormData = config.data instanceof FormData;
      const currentUser = auth.currentUser;

      if (currentUser) {
        try {
          // Cached token; the SDK refreshes it only when expired or about to expire
          const token = await currentUser.getIdToken();
          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          console.error("Error getting auth token:", error);
          throw new Error("Authentication failed. Please sign in again.");
        }
      }

      if (!isFormData && config.data) {
        config.headers["Content-Type"] = "application/json";
      }

      return config;
    },
    (error: unknown) => {
      return Promise.reject(error);
    },
  );

  // Add response interceptor for error handling
  instance.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      // On 401, force-refresh the token once and retry (e.g. revoked token or clock skew)
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        const originalConfig = error.config as
          | (typeof error.config & { _retried?: boolean })
          | undefined;
        const currentUser = auth.currentUser;
        if (originalConfig && !originalConfig._retried && currentUser) {
          originalConfig._retried = true;
          try {
            const freshToken = await currentUser.getIdToken(true);
            originalConfig.headers.Authorization = `Bearer ${freshToken}`;
            return instance.request(originalConfig);
          } catch (refreshError) {
            console.error("Token refresh after 401 failed:", refreshError);
          }
        }
      }

      // Capture API errors to Sentry only in production
      if (
        import.meta.env.VITE_APP_ENV === "prd" &&
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof error.response === "object" &&
        error.response !== null
      ) {
        try {
          const response = error.response as any;
          const config = (error as any).config;

          // Extract error details with fallbacks
          const status = response?.status || "UNKNOWN";
          const method = config?.method?.toUpperCase() || "UNKNOWN";
          const url = config?.url || "unknown-url";

          // Don't capture expected 404 errors for normal application flow
          const is404 = response?.status === 404;
          const isUserValidation = config?.url?.includes(
            "/users/login_user_access",
          );
          const isUserFetch = config?.url?.includes("/users/external/");
          const isNdaStatus =
            config?.url?.includes("/signatures/") &&
            config?.url?.includes("/nda_status");
          const isExpected404 =
            is404 && (isUserValidation || isUserFetch || isNdaStatus);

          // Only capture errors that are not part of normal application flow
          if (!isExpected404) {
            Sentry.captureException(error, {
              tags: {
                api_status: status,
                api_method: method,
                error_type: "api_error",
              },
              contexts: {
                request: {
                  method: method,
                  url: url,
                  baseURL: config?.baseURL,
                  headers: config?.headers,
                  params: config?.params,
                },
                response: {
                  status: status,
                  statusText: response?.errors?.errorDetail,
                  data: response?.data,
                },
              },
              extra: {
                requestPayload: config?.data, // The actual request body/payload
                axiosErrorMessage: (error as any).message,
                axiosErrorCode: (error as any).code,
              },
            });
          }
        } catch (sentryError) {
          // Prevent Sentry errors from breaking the app
          console.error("Failed to capture error in Sentry:", sentryError);
        }
      }

      // Log 401 errors specifically
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof error.response === "object" &&
        error.response !== null &&
        (error.response as any).status === 401
      ) {
        console.error("Unauthorized request. Token may be expired.");
      }

      return Promise.reject(error);
    },
  );

  return instance;
}

/**
 * Context for the axios client
 */
interface ApiClientContextValue {
  client: AxiosInstance | null;
  isReady: boolean;
}

const ApiClientContext = createContext<ApiClientContextValue | undefined>(
  undefined,
);

/**
 * Props for the ApiClientProvider
 */
interface ApiClientProviderProps {
  children: ReactNode;
  baseURL: string;
}

/**
 * Provider component to initialize and provide the axios client
 */
export function ApiClientProvider({
  children,
  baseURL,
}: ApiClientProviderProps) {
  const [client, setClient] = useState<AxiosInstance | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function initClient() {
      try {
        const axiosInstance = await createAuthenticatedAxiosInstance(baseURL);
        if (mounted) {
          setClient(() => axiosInstance);
          setIsReady(true);
        }
      } catch (error) {
        console.error("Failed to initialize API client:", error);
      }
    }

    initClient();

    return () => {
      mounted = false;
    };
  }, [baseURL]);

  const value = useMemo(
    () => ({
      client,
      isReady,
    }),
    [client, isReady],
  );

  return (
    <ApiClientContext.Provider value={value}>
      {children}
    </ApiClientContext.Provider>
  );
}

/**
 * Hook to access the axios client from context
 * @returns The axios client instance
 * @throws Error if used outside of ApiClientProvider
 */
export function useApiClient(): AxiosInstance {
  const context = useContext(ApiClientContext);

  if (context === undefined) {
    throw new Error("useApiClient must be used within an ApiClientProvider");
  }

  if (!context.client) {
    throw new Error("API client is not initialized yet");
  }

  return context.client;
}

/**
 * Hook to check if the API client is ready
 * @returns Object with client and isReady status
 */
export function useApiClientStatus(): ApiClientContextValue {
  const context = useContext(ApiClientContext);

  if (context === undefined) {
    throw new Error(
      "useApiClientStatus must be used within an ApiClientProvider",
    );
  }

  return context;
}
